// src/components/Organization/EmailUnsubscribeList.tsx

// What? Admin view of the org's unsubscribe list (R-9 Phase 1).
// Why? Admins need to audit who has opted out, see how each entry landed
//      there (one-click header, link click, manual, complaint), and add /
//      remove rows as needed when users request it by phone or email.
// How? Mirrors the Email Layout / Email Templates card structure used
//      elsewhere in Org Settings. Respects the same `useOrgEmailStatus`
//      overlay so a disabled org shows this panel dimmed rather than
//      hidden.

import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Input,
  Sheet,
  Stack,
  Table,
  Typography,
} from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MailLockIcon from "@mui/icons-material/MailLock";
import { useAuthFetch } from "../../utils/useAuthFetch";
import { useOrgEmailStatus } from "../../hooks/useOrgEmailStatus";
import DisabledEmailOverlay from "./DisabledEmailOverlay";

interface Props {
  organizationId: string;
}

interface UnsubscribeEntry {
  id: string;
  recipient_email: string;
  template_key: string | null;
  source: string;
  created_at: string;
}

const SOURCE_COLORS: Record<
  string,
  "success" | "warning" | "danger" | "neutral"
> = {
  manual_admin: "neutral",
  link_click: "neutral",
  header_one_click: "success",
  complaint: "danger",
};

const EmailUnsubscribeList: React.FC<Props> = ({ organizationId }) => {
  const authFetch = useAuthFetch();
  const { enabled: emailEnabled, loading: emailStatusLoading } =
    useOrgEmailStatus(organizationId);
  const [entries, setEntries] = useState<UnsubscribeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{
    type: "success" | "danger";
    message: string;
  } | null>(null);

  const [newEmail, setNewEmail] = useState("");
  const [newTemplateKey, setNewTemplateKey] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const data = await authFetch(
        `/api/email/organization/${organizationId}/unsubscribes`,
        "fetch unsubscribes",
      );
      if (Array.isArray(data)) setEntries(data);
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load unsubscribes",
      );
    } finally {
      setLoading(false);
    }
  }, [authFetch, organizationId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleAdd = async () => {
    const trimmed = newEmail.trim();
    if (!trimmed || !trimmed.includes("@")) return;
    setBusy(true);
    setFeedback(null);
    try {
      const payload: Record<string, unknown> = { recipient_email: trimmed };
      if (newTemplateKey.trim()) payload.template_key = newTemplateKey.trim();
      await authFetch(
        `/api/email/organization/${organizationId}/unsubscribes`,
        "add unsubscribe",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      setNewEmail("");
      setNewTemplateKey("");
      setFeedback({ type: "success", message: "Added" });
      await refresh();
    } catch (err) {
      setFeedback({
        type: "danger",
        message: err instanceof Error ? err.message : "Failed to add",
      });
    } finally {
      setBusy(false);
      setTimeout(() => setFeedback(null), 4000);
    }
  };

  const handleRemove = async (entry: UnsubscribeEntry) => {
    setBusy(true);
    setFeedback(null);
    try {
      const params = new URLSearchParams({
        recipient_email: entry.recipient_email,
      });
      if (entry.template_key) params.set("template_key", entry.template_key);
      await authFetch(
        `/api/email/organization/${organizationId}/unsubscribes?${params.toString()}`,
        "remove unsubscribe",
        { method: "DELETE" },
      );
      setFeedback({ type: "success", message: "Removed" });
      await refresh();
    } catch (err) {
      setFeedback({
        type: "danger",
        message: err instanceof Error ? err.message : "Failed to remove",
      });
    } finally {
      setBusy(false);
      setTimeout(() => setFeedback(null), 4000);
    }
  };

  const body = (
    <Stack spacing={2}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <MailLockIcon sx={{ color: "primary.500" }} />
        <Typography level="title-lg">Unsubscribe list</Typography>
        <Typography level="body-xs" sx={{ color: "text.tertiary", ml: "auto" }}>
          {entries.length} {entries.length === 1 ? "entry" : "entries"}
        </Typography>
      </Box>
      <Typography level="body-sm" sx={{ color: "text.secondary" }}>
        Recipients on this list are skipped whenever a send from this org
        matches the entry&apos;s scope. Org-wide entries (no template) catch
        every send; template-scoped entries only block that one template.
      </Typography>

      {error && <Alert color="danger">{error}</Alert>}
      {feedback && <Alert color={feedback.type}>{feedback.message}</Alert>}

      <Card variant="soft">
        <CardContent>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <FormControl sx={{ flex: 2 }}>
              <FormLabel>Recipient email</FormLabel>
              <Input
                size="sm"
                type="email"
                placeholder="user@example.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
            </FormControl>
            <FormControl sx={{ flex: 2 }}>
              <FormLabel>Template (optional)</FormLabel>
              <Input
                size="sm"
                placeholder="e.g. bkg.digest_delivery"
                value={newTemplateKey}
                onChange={(e) => setNewTemplateKey(e.target.value)}
              />
              <FormHelperText>
                Leave blank to unsubscribe from every template for this org.
              </FormHelperText>
            </FormControl>
            <Box sx={{ alignSelf: "flex-end" }}>
              <Button
                size="sm"
                startDecorator={<AddIcon />}
                loading={busy}
                onClick={handleAdd}
                disabled={!newEmail.trim() || !newEmail.includes("@")}
              >
                Add
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size="sm" />
        </Box>
      ) : entries.length === 0 ? (
        <Typography
          level="body-sm"
          sx={{ color: "text.tertiary", textAlign: "center", py: 3 }}
        >
          No unsubscribes yet.
        </Typography>
      ) : (
        <Sheet
          variant="outlined"
          sx={{ borderRadius: "sm", overflow: "hidden" }}
        >
          <Table size="sm" stickyHeader>
            <thead>
              <tr>
                <th style={{ width: "40%" }}>Email</th>
                <th style={{ width: "25%" }}>Template</th>
                <th style={{ width: "15%" }}>Source</th>
                <th style={{ width: "15%" }}>Added</th>
                <th style={{ width: "5%" }} />
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td>
                    <Typography level="body-sm">
                      {entry.recipient_email}
                    </Typography>
                  </td>
                  <td>
                    <Typography
                      level="body-xs"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {entry.template_key || "(all)"}
                    </Typography>
                  </td>
                  <td>
                    <Chip
                      size="sm"
                      variant="soft"
                      color={SOURCE_COLORS[entry.source] || "neutral"}
                    >
                      {entry.source}
                    </Chip>
                  </td>
                  <td>
                    <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                      {new Date(entry.created_at).toLocaleDateString()}
                    </Typography>
                  </td>
                  <td>
                    <IconButton
                      size="sm"
                      variant="plain"
                      color="danger"
                      onClick={() => handleRemove(entry)}
                      disabled={busy}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Sheet>
      )}
    </Stack>
  );

  return (
    <Card variant="outlined">
      <CardContent>
        {!emailStatusLoading && !emailEnabled ? (
          <DisabledEmailOverlay>{body}</DisabledEmailOverlay>
        ) : (
          body
        )}
      </CardContent>
    </Card>
  );
};

export default EmailUnsubscribeList;
